<template>
	<div
		class="InputRichDropdown"
		ref="InputRichDropdown"
		:class="cssClass"
		@keydown="onKeyDown"
		@focusout="onFocusOut"
		@focusin="
			() => {
				focused = true;
			}
		"
	>
		<InputTextbox
			:saturn="displaySelectedItems"
			:dropdown="true"
			:label="label"
			type="text"
			:invalid="invalid"
			:disabled="disabled"
			:dropdownFocused="isOpen || focused"
			:width="width"
			:height="height"
			:infoIconObj
			ref="InputTextbox"
		>
			<template #dropdown>
				<div
					class="ird-container"
					:class="{ active: isOpen }"
					:title="activeTitle"
					@click.stop="
						e => {
							focusIrdSelect();
							toggleOpen();
						}
					"
					@mousedown.prevent.stop=""
					@keydown.enter.prevent.stop="toggleOpen()"
					@keydown.space="
						e => {
							if (!isOpen) {
								toggleOpen();
								e.preventDefault();
								e.stopPropagation();
							}
						}
					"
				>
					<div class="ird-header">
						<div
							v-if="isOpen && multiSelect && selectAll"
							class="icon-container"
							:title="isAnythingSelected ? 'Clear all' : 'Select all'"
							@click.stop="selectAllItems(!isAnythingSelected)"
						>
							<InputCheckbox
								:modelValue="isAnythingSelected"
								:visualOnly="true"
							/>
						</div>

						<input
							v-if="isOpen && search"
							v-model="searchText"
							ref="searchInput"
							class="search-input"
							placeholder="Search"
							@keydown.enter.prevent.stop="selectSearchedItem()"
							@click.stop="focusSearchInput()"
							@input="onSearchHandler"
						/>
						<template v-else>
							<div
								v-if="$slots.selected"
								class="icon-container"
							>
								<slot
									name="selected"
									v-bind="saturn"
								></slot>
							</div>
							<span class="ird-header-label">
								{{ displaySelectedItems }}
							</span>
						</template>
					</div>

					<select
						:disabled="disabled"
						ref="irdSelect"
						@keydown.space.prevent=""
					>
						<!-- This option exists ONLY to allow InputRichDropdown to have a dynamic width based on its longest item -->
						<option selected>{{ longestItem }}</option>
					</select>

					<transition name="ird-transition">
						<div
							v-if="isOpen"
							ref="irdItems"
							class="ird-items"
							:class="{ overflow: overflow }"
							:style="{
								...dropdownSizeAndPosition,
								'max-height': dropDownMaxHeight,
							}"
							tabindex="-1"
						>
							<div
								v-if="nothingSelected && !searchText"
								class="ird-item no-item"
								tabindex="-1"
								@click.stop="updateSaturnToNull()"
								@keydown.enter.prevent.stop="updateSaturnToNull()"
							>
								<span class="ird-label">{{ nothingSelected }}</span>
							</div>

							<div
								v-for="(item, index) in loadedItems"
								:key="item.id || item.$index"
								class="ird-item value-item"
								:class="{ 'bubbled-item': item.isBubbled }"
								:title="display(item.obj) + (desc ? ' - ' + desc(item.obj) : '')"
								tabindex="-1"
								:disabled="itemIsDisabled(item.obj)"
								:style="{ ...customItemStyle(item.obj), height: height + 'px' }"
								@click.stop="toggleItem(item)"
								@keydown.enter.prevent.stop="toggleItem(item)"
							>
								<div class="icon-container">
									<slot
										v-if="$slots.item"
										name="item"
										v-bind="item"
									></slot>
									<InputCheckbox
										v-else-if="multiSelect"
										:modelValue="item.isSelected"
										:visualOnly="true"
									/>
									<InputRadio
										v-else
										:modelValue="item.isSelected"
										:valueMatch="true"
										:visualOnly="true"
									/>
								</div>
								<slot
									v-if="iconCondition?.(item.obj)"
									name="item-icon"
								>
								</slot>
								<span class="ird-label">{{ display(item.obj) }}</span>
								<span
									v-if="desc && desc(item.obj)"
									class="ird-desc"
									>{{ desc(item.obj) }}</span
								>
							</div>

							<div
								v-if="lazyLoad && !finishedLoadingItems"
								class="loading-items"
								v-seen="() => pagesLoaded++"
							>
								<i class="fas fa-circle-notch fa-spin"></i>
							</div>

							<div
								v-if="showAddCustomItemButton"
								class="ird-item help-item"
								tabindex="-1"
								@click.stop="selectSearchedItem(true)"
								@keydown.enter.stop="selectSearchedItem(true)"
							>
								<div class="icon-container">
									<i class="fas fa-plus-circle"></i>
								</div>
								<span class="ird-label">{{ customItemHint }}</span>
							</div>
							<div
								v-else-if="!foundAnySearchItem"
								class="ird-item no-item"
								style="cursor: default"
							>
								<span class="ird-label">None found</span>
							</div>
						</div>
					</transition>
				</div>
			</template>
			<template
				v-if="util.hasKey($slots, 'validation')"
				#validation
			>
				<slot name="validation"></slot>
			</template>
			<template
				#action
				v-if="util.hasKey($slots, 'action')"
			>
				<slot name="action"></slot>
			</template>
		</InputTextbox>
	</div>
</template>
<script>
	import _ from 'underscore';
	import ENUMS from '@core/classes/Enums';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import IntersectionObserverDirective from '@core/directives/intersection-observer-directive';
	import util from '@core/services/util';

	export default {
		name: 'InputRichDropdown',
		emits: ['change', 'blur', 'update:saturn', 'search'],
		props: {
			// Any data that represents the value of the initial selected item(s). Use .sync for two-way data binding.
			saturn: {},
			// A nullable array of data that represents the list of dropdown items.
			// Note: If value and comparer props are default AND list is an array of objects, list must NOT be generated by an inline function call.
			list: {
				required: true,
				validator: list => list === null || Array.isArray(list),
			},
			valueMap: {
				type: Function,
				default: x => x,
			},
			display: {
				type: Function,
				default: x => `${x}`,
			},
			// A function that compares two arguments that match the shape of a list item's value. Must return a boolean.
			comparer: {
				type: Function,
				default: (x, y) => x === y,
			},
			// A function that takes in data and transforms it to match the shape of an item in the list prop. Must ALWAYS be able to handle an input of type string (from searching).
			customHandling: {
				type: Function,
				default: null,
			},
			disabled: {
				type: [Boolean, Function],
				default: false,
			},
			nothingSelected: {
				type: String,
				default: '',
			},
			selectAll: {
				type: Boolean,
				default: false,
			},
			label: {
				type: String,
				default: '',
			},
			search: {
				type: Boolean,
				default: false,
			},
			overflow: {
				type: Boolean,
				default: true,
			},
			multiSelect: {
				type: Boolean,
				default: false,
			},
			invalid: {
				type: Boolean,
				default: false,
			},
			desc: {
				type: Function,
				default: null,
			},
			iconCondition: {
				type: Function,
				default: null,
			},
			cssClass: {
				type: String,
				default: '',
			},
			width: {
				type: Number,
				required: false,
			},
			height: {
				type: Number,
				required: false,
			},
			// The maximum height of a dropdown container. Multiples of height prop looks best.
			containerHeight: {
				type: Number,
				default: 280,
			},
			// An enum type that chooses the way the dropdown list gets sorted. Use CUSTOM if you want to pass in a sortCompare.
			sortMode: {
				type: Number,
				default: ENUMS.DROPDOWN_SORT_MODE.ASCENDING,
				validator: sortMode => Object.values(ENUMS.DROPDOWN_SORT_MODE).includes(sortMode),
			},
			// Defines custom function to use for sorting dropdown list. Should only be passed when sortMode is CUSTOM.
			sortCompare: {
				type: Function,
				required: false,
			},
			customItemStyle: {
				type: Function,
				default: () => new Object(),
			},
			customItemHintHandler: {
				type: Function,
				default: searchText => `Add "${searchText}" as value`,
			},
			customTitle: {
				type: String,
				default: null,
			},
			lazyLoad: {
				type: Boolean,
				default: false,
			},
			pageSize: {
				type: Number,
				default: 20,
			},
			infoIconObj: {
				type: Object,
				default: null,
			},
		},
		data() {
			return {
				vm: {
					saturn: null,
					list: null,
				},
				searchText: null,
				isOpen: false,
				dropdownSizeAndPosition: {},
				dropDownMaxHeight: `${this.containerHeight}px`,
				focused: false,
				pagesLoaded: 1,
			};
		},
		created() {
			if (this.customHandling && !this.search) {
				console.warn('InputRichDropdown component: customHandling is enabled, but search prop is not. Enable it for customHandling to work.');
			}
		},
		mounted() {
			this.updateViewModel();

			this.$watch(
				() => this.list,
				() => {
					this.updateViewModel();
				},
				{ deep: true },
			);

			this.$watch(
				() => this.saturn,
				() => {
					this.updateViewModel();
				},
				{ deep: true },
			);

			if (this.overflow) {
				this.$watch(
					() => this.isOpen,
					newValue => {
						if (newValue) {
							window.addEventListener('scroll', this.setDropdownSizeAndPosition, true);
							window.addEventListener('resize', this.setDropdownSizeAndPosition);
						} else {
							window.removeEventListener('scroll', this.setDropdownSizeAndPosition, true);
							window.removeEventListener('resize', this.setDropdownSizeAndPosition);
						}
					},
				);
			}
		},
		beforeUnmount() {
			window.removeEventListener('scroll', this.setDropdownSizeAndPosition, true);
			window.removeEventListener('resize', this.setDropdownSizeAndPosition);
		},
		computed: {
			util() {
				return util;
			},
			searchBy() {
				return this.searchText?.trim().toLowerCase();
			},
			selectedItems() {
				return this.vm.list.filter(i => i.isSelected);
			},
			displaySelectedItems() {
				if (this.isAnythingSelected) {
					let items = [...this.selectedItems];
					if (this.sortResults) {
						items.sort(this.sortResults);
					}
					return items.map(i => this.display(i.obj)).join(', ');
				}
				return this.nothingSelected;
			},
			isAnythingSelected() {
				return this.vm.list && this.vm.list.some(i => i.isSelected == true);
			},
			longestItem() {
				return (this.vm.list ?? []).reduce((acc, item) => {
					const curr = this.display(item.obj);

					return curr?.length > acc.length ? curr : acc;
				}, this.nothingSelected);
			},
			customItemHint() {
				return this.customItemHintHandler(this.searchText);
			},
			filteredItemsBySearchQuery() {
				let filteredItems = this.vm.list ? [...this.vm.list] : null;

				if (filteredItems) {
					if (this.searchBy) {
						filteredItems = filteredItems.filter(i => {
							const textToSearch = this.display(i.obj) + (this.desc ? ' ' + this.desc(i.obj) : '');

							return textToSearch.toLowerCase().search(this.searchBy) > -1;
						});
					}

					if (this.sortResults) {
						filteredItems.sort(this.sortResults);
					}
					// Push bubbled items to the front of filtered list
					filteredItems = _.partition(filteredItems, i => i.isBubbled).flat();
				}

				return filteredItems;
			},
			loadedItems() {
				if (!this.lazyLoad) return this.filteredItemsBySearchQuery;

				return this.filteredItemsBySearchQuery.slice(0, this.pagesLoaded * this.pageSize);
			},
			finishedLoadingItems() {
				return this.pagesLoaded >= Math.ceil(this.filteredItemsBySearchQuery.length / this.pageSize);
			},
			foundExactSearchItem() {
				const foundItem = this.filteredItemsBySearchQuery?.[0];
				return foundItem ? this.display(foundItem.obj).toLowerCase() === this.searchBy : false;
			},
			foundAnySearchItem() {
				return !!this.filteredItemsBySearchQuery?.length;
			},
			showAddCustomItemButton() {
				return this.customHandling && !this.foundAnySearchItem && this.searchText && this.customItemHint;
			},
			sortResults() {
				switch (this.sortMode) {
					case ENUMS.DROPDOWN_SORT_MODE.ASCENDING:
						return (a, b) => this.display(a.obj).toLowerCase().localeCompare(this.display(b.obj).toLowerCase());
					case ENUMS.DROPDOWN_SORT_MODE.DESCENDING:
						return (b, a) => this.display(a.obj).toLowerCase().localeCompare(this.display(b.obj).toLowerCase());
					case ENUMS.DROPDOWN_SORT_MODE.CUSTOM:
						if (!this.sortCompare) throw new Error('Compare function is null');
						return this.sortCompare;
					case ENUMS.DROPDOWN_SORT_MODE.ORIGINAL_ORDER:
					default:
						return null;
				}
			},
			activeTitle() {
				return this.customTitle ?? this.displaySelectedItems;
			},
		},
		methods: {
			onKeyDown(e) {
				if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
					const dir = e.key === 'ArrowUp' ? -1 : 1;
					const items = [...this.$refs.irdItems.querySelectorAll('.ird-item[tabIndex="-1"]:not([disabled="true"])')];

					// Clamp target index to be within array bounds
					let targetIndex = items.findIndex(el => el === document.activeElement) + dir;
					targetIndex = Math.max(0, Math.min(items.length - 1, targetIndex));

					items[targetIndex]?.focus();
					e.preventDefault();
				} else if (e.key === 'Escape' || e.key === 'Tab') {
					if (this.isOpen) {
						this.toggleOpen(false);
						this.focusIrdSelect();
						e.preventDefault();
					}
				} else if (e.target !== this.$refs.searchInput && (e.key === 'Backspace' || e.key.length === 1)) {
					this.focusSearchInput();
				}
			},
			onFocusOut(e) {
				if (!this.$refs.InputRichDropdown?.contains(e.relatedTarget)) {
					this.toggleOpen(false);
					this.focused = false;
					this.$emit('blur');
				}
			},
			onSearchHandler() {
				this.pagesLoaded = 1;
				this.$emit('search', this.searchText);
			},
			focusSearchInput() {
				this.$refs.searchInput?.focus();
			},
			focusIrdSelect() {
				this.$refs.irdSelect?.focus();
			},
			updateSaturnToNull() {
				this.toggleOpen(false);
				this.focusIrdSelect();
				this.$emit('update:saturn', null);
				this.$emit('change', null, null);
			},
			updateSaturn() {
				let items = [...this.selectedItems];
				let values = items.map(i => this.valueMap(i.obj));

				if (!this.multiSelect) {
					if (items.length > 0) {
						items = items[0];
						values = values[0];
					} else {
						items = null;
						values = null;
					}
				}

				// TODO: emit input event?
				this.$emit('update:saturn', values);
				this.$emit('change', values, items);
			},
			updateViewModel() {
				this.vm.saturn = this.saturn;

				const customItems = this.vm.list ? this.vm.list.filter(i => i.isCustom) : null;

				this.vm.list = (this.list ?? []).map((listItem, index) => {
					let item = { obj: listItem };

					if (this.multiSelect) {
						item.isSelected = Array.isArray(this.saturn) && this.saturn.some(s => this.comparer(s, this.valueMap(listItem)));
					} else {
						item.isSelected = this.comparer(this.saturn, this.valueMap(listItem));
					}

					const foundItem = this.vm.list?.find(i => this.comparer(this.valueMap(listItem), this.valueMap(i.obj)));
					if (foundItem) {
						item.isBubbled = foundItem.isBubbled;
					}

					return item;
				});

				if (this.customHandling && this.saturn != null) {
					if (customItems) {
						this.vm.list = this.vm.list.concat(
							// Add existing custom items whose value does not match with any values in list prop
							customItems
								.filter(i => !this.vm.list.some(x => this.comparer(this.valueMap(x.obj), this.valueMap(i.obj))))
								.map(i => {
									return { ...i, isSelected: false, isCustom: true };
								}),
						);
					}

					// Normalize saturn to array if not one already
					let items = [].concat(this.saturn);

					// Truncate array if multiSelect is off so that items contains 1 element
					if (!this.multiSelect) {
						items.length = 1;
					}

					// Add new custom items for every saturn value that does not exist in list prop
					items.forEach(i => {
						if (!this.vm.list.some(x => this.comparer(i, this.valueMap(x.obj)))) {
							this.vm.list.push({
								isSelected: true,
								isCustom: true,
								obj: this.customHandling(i),
							});
						}
					});

					this.vm.list.forEach(i => {
						i.isSelected = items.some(item => this.comparer(item, this.valueMap(i.obj)));
					});
				}

				if (this.selectedItems.length > 0) {
					if (this.multiSelect) {
						this.vm.saturn = this.selectedItems;
					} else {
						this.vm.saturn = this.selectedItems[0];
					}
				}
			},
			setDropdownSizeAndPosition() {
				const rect = this.$refs.InputTextbox.$el.getBoundingClientRect();

				const widthDiff = window.innerWidth - rect.left;
				const heightDiff = window.innerHeight - rect.bottom;
				this.dropDownMaxHeight = `${Math.min(heightDiff - 15, this.containerHeight)}px`;

				this.dropdownSizeAndPosition = {
					left: `${rect.left}px`,
					top: `${Math.max(rect.bottom, 15) - 1}px`,
					minWidth: `${rect.right - rect.left - 2}px`, // minus 2 for left/right borders
					maxWidth: `${widthDiff - 15}px`,
				};
			},
			toggleOpen(val) {
				if (this.disabled) return;

				if (typeof val === 'boolean') {
					this.isOpen = val;
				} else {
					this.isOpen = !this.isOpen;
				}

				// Opening dropdown
				if (this.isOpen === true) {
					// Bubble selected items and filter out unselected custom items
					this.vm.list.forEach(i => {
						i.isBubbled = i.isSelected;
					});
					this.vm.list = this.vm.list.filter(i => !i.isCustom || i.isSelected);

					if (this.overflow) {
						this.setDropdownSizeAndPosition();
					}

					this.$nextTick(() => {
						if (this.search) {
							this.focusSearchInput();
						} else {
							this.focusIrdSelect();
						}
					});
				}
				// Closing dropdown
				else {
					this.pagesLoaded = 1;
					this.searchText = null;
				}
			},
			selectSearchedItem(forceCustomItem = false) {
				if (util.isNullOrEmpty(this.searchBy)) return;

				const searchText = this.searchText;
				const foundItem = this.filteredItemsBySearchQuery?.[0];

				if ((!foundItem && !this.customHandling) || (foundItem && this.itemIsDisabled(foundItem.obj))) return;

				if (this.multiSelect) {
					this.focusSearchInput();
				} else {
					this.selectAllItems(false);
					this.toggleOpen(false);
					this.focusIrdSelect();
				}

				if (foundItem && !forceCustomItem) {
					foundItem.isSelected = true;
				} else if (this.customHandling) {
					this.vm.list.push({
						isSelected: true,
						isBubbled: true,
						isCustom: true,
						obj: this.customHandling(searchText),
					});
				}

				this.searchText = null;
				this.vm.saturn = this.selectedItems;
				this.updateSaturn();
			},
			toggleItem(item) {
				if (this.itemIsDisabled(item.obj)) return;

				if (!this.multiSelect) {
					if (this.selectedItems.length > 0) {
						if (this.selectedItems[0] == item) return;
						this.selectedItems[0].isSelected = false;
					}

					this.searchText = null;
					this.toggleOpen(false);
					this.focusIrdSelect();
				}

				item.isSelected = !item.isSelected;

				if (this.selectedItems.length > 0) {
					this.vm.saturn = this.selectedItems;
				} else {
					this.vm.saturn = null;
				}

				this.updateSaturn();
			},
			selectAllItems(isSelected) {
				if (this.searchText) {
					this.searchText = null;
				}

				this.vm.list.filter(i => !this.itemIsDisabled(i.obj)).forEach(i => (i.isSelected = isSelected));

				if (this.multiSelect) {
					this.updateSaturn();
				}
			},
			itemIsDisabled(item) {
				return util.isFunction(this.disabled) ? this.disabled(item) : item.disabled;
			},
		},
		directives: {
			seen: IntersectionObserverDirective,
		},
		components: {
			InputTextbox,
			InputCheckbox,
			InputRadio,
		},
	};
</script>

<style>
	/* General */

	.InputRichDropdown {
		font-weight: normal;
	}

	.InputRichDropdown .icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 12px;
		flex-shrink: 0;
	}

	.InputRichDropdown .ird-items .icon-container {
		font-size: 14px;
	}

	.InputRichDropdown .ird-header {
		position: relative;
		display: flex;
		padding-inline: 10px;
		gap: 10px;
		align-items: center;
		height: 100%;
	}

	.InputRichDropdown .ird-header .ird-label {
		display: flex;
		align-items: center;
	}

	.InputRichDropdown .ird-header .ird-clear {
		position: absolute;
		cursor: pointer;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
	}

	.InputRichDropdown .ird-header input.search-input {
		margin-right: 24px !important;
		flex-grow: 1;
		width: 0;
		border: 1px solid var(--border-color);
		border-radius: 4px;
		transition: all 0.2s linear;
		cursor: text;
		padding: 0;
	}

	.InputRichDropdown .ird-container {
		width: 100%;
		height: 100%;
	}

	.InputRichDropdown .ird-container select {
		padding: 0 20px 0 32px;
		height: 0;
		opacity: 0;
		border: none;
		white-space: nowrap;
		max-width: 100%;
	}

	.InputRichDropdown .ird-container select > option {
		font-size: inherit;
	}

	.InputRichDropdown .ird-items {
		background-color: var(--input-multiselect-dropdown-background-color);
		z-index: 999999;
		position: absolute;
		box-sizing: content-box;
		left: 0px;
		min-width: calc(100% - 2px);
		top: calc(100% - 1px);
		cursor: default;
		overflow: hidden;
		transform-origin: top;
		overflow-y: auto;
		border: 1px solid var(--main-color);
		border-radius: 4px;
		box-shadow: 0 4px 16px 0 var(--black-20percent);
	}

	.InputRichDropdown .ird-items.overflow {
		position: fixed;
		left: unset;
		right: unset;
		top: unset;
	}

	/* InputRichDropdown Item */

	.ird-item {
		display: flex;
		gap: 10px;
		align-items: center;
		justify-content: flex-start;
		width: 100%;
		cursor: pointer;
		height: 40px;
		padding-inline: 10px;
		transition: var(--transition);
		transition-duration: 0.1s;
		background-color: var(--background-color);
		box-sizing: border-box;
	}

	.ird-item[disabled='true'] {
		cursor: not-allowed;
		opacity: 50%;
	}

	.ird-item:not(:first-child) {
		border-top: 1px solid var(--input-rich-dropdown-item-border-color);
	}

	.ird-item.no-item + .ird-item {
		border-top: none;
	}

	.ird-item.no-item {
		font-style: italic;
	}

	.ird-item.no-item:not(:last-child) {
		border-bottom: 2px solid var(--input-rich-dropdown-item-separator-color);
	}

	.ird-item.no-item:not(:first-child) {
		border-top: 2px solid var(--input-rich-dropdown-item-separator-color);
	}

	.ird-item.no-item .ird-label {
		overflow: visible;
	}

	.ird-header .ird-header-label {
		width: 0;
		flex-grow: 1;
		text-overflow: clip;
		white-space: nowrap;
		overflow: hidden;
	}

	.ird-item.help-item {
		position: sticky;
		bottom: 0px;
		color: var(--main-color);
		font-weight: bold;
	}

	.ird-item.help-item:not(:first-child) {
		border-top: 2px solid var(--input-rich-dropdown-item-separator-color);
	}

	.InputRichDropdown .ird-items .ird-item.bubbled-item + .ird-item:not(.bubbled-item) {
		border-top: 2px solid var(--input-rich-dropdown-item-separator-color);
	}

	.ird-items .ird-label {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.ird-items .ird-desc {
		color: #a9a9a9;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.ird-item:hover,
	.ird-item:focus {
		box-shadow: inset 0 0 0 100vw var(--black-10percent);
	}

	/* Vue Transitions */

	.ird-transition-enter-active {
		opacity: 1;
		transition:
			opacity 0.1s ease-out,
			transform 0.1s ease-out;
	}

	.ird-transition-leave-active {
		opacity: 1;
		transition:
			opacity 0.1s ease-in,
			transform 0.1s ease-in;
	}

	.ird-transition-enter-from,
	.ird-transition-leave-to {
		opacity: 0;
		overflow: hidden;
		transform: scaleY(0);
	}

	/* InputTextbox Overrides */

	.InputRichDropdown .input-component.dropdown.focused .saturn-textbox .saturn-icon {
		transform: unset;
	}

	.InputRichDropdown:has(.ird-container.active) .input-component.dropdown.focused .saturn-textbox .saturn-icon {
		transform: rotate(180deg);
	}

	.InputRichDropdown .saturn-textbox {
		cursor: pointer;
	}

	.InputRichDropdown:has(:disabled) .saturn-textbox {
		cursor: default;
		background-color: var(--select-background-color-disabled);
		color: var(--select-color-disabled);
	}

	.InputRichDropdown:has(:disabled) .ird-header {
		pointer-events: none;
	}

	.InputRichDropdown .saturn-textbox:has(select:disabled) .saturn-icon i {
		color: var(--select-color-disabled);
	}

	.InputRichDropdown select:disabled {
		cursor: default;
		background-color: unset;
		color: unset;
		opacity: 1;
	}

	/* Intersection observer element */

	.InputRichDropdown .loading-items {
		color: var(--main-color);
		height: 80px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* While opening/closing dropdown, hide to prevent triggering intersection observer */
	.InputRichDropdown .ird-transition-enter-active .loading-items,
	.InputRichDropdown .ird-transition-leave-active .loading-items {
		display: none;
	}
</style>
